import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { searchOpenLibrary } from '@/lib/searchUtils';
import type { BookResult } from '@/types/search';

interface UseBookSearchOptions {
  limit?: number;
  enableFallback?: boolean;
}

interface UseBookSearchReturn {
  results: BookResult[];
  isLoading: boolean;
  error: string | null;
  search: (query: string, type?: 'title' | 'author') => Promise<void>;
  clear: () => void;
}

/**
 * Unified book search hook used across the app.
 * Provides consistent behavior: edge function first, Open Library fallback.
 * No aggressive client-side filtering - trusts the edge function's ranking.
 */
export function useBookSearch(options: UseBookSearchOptions = {}): UseBookSearchReturn {
  const { limit = 20, enableFallback = true } = options;
  
  const [results, setResults] = useState<BookResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = useCallback(async (query: string, type: 'title' | 'author' = 'title') => {
    const trimmedQuery = query.trim();
    
    if (!trimmedQuery || trimmedQuery.length < 2) {
      setResults([]);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Primary: Edge function search (handles local DB + external sources)
      const { data, error: fnError } = await supabase.functions.invoke('search-books', {
        body: { query: trimmedQuery, type, page: 1, limit },
      });

      if (fnError) throw fnError;

      if (data?.results?.length > 0) {
        setResults(data.results);
        return;
      }

      // Fallback: Direct Open Library search when edge function returns 0 results
      if (enableFallback) {
        console.log('[useBookSearch] Edge function returned 0 results, trying Open Library fallback...');
        const { results: olResults } = await searchOpenLibrary(trimmedQuery, type, 1, limit);
        setResults(olResults);
        return;
      }

      setResults([]);
    } catch (err) {
      console.error('[useBookSearch] Search error:', err);
      
      // Error fallback: Try Open Library if edge function failed
      if (enableFallback) {
        try {
          console.log('[useBookSearch] Edge function failed, trying Open Library fallback...');
          const { results: olResults } = await searchOpenLibrary(trimmedQuery, type, 1, limit);
          setResults(olResults);
          setError(null);
          return;
        } catch (fallbackErr) {
          console.error('[useBookSearch] Fallback also failed:', fallbackErr);
        }
      }
      
      setError('Search failed. Please try again.');
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, [limit, enableFallback]);

  const clear = useCallback(() => {
    setResults([]);
    setError(null);
  }, []);

  return {
    results,
    isLoading,
    error,
    search,
    clear,
  };
}
